import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight } from "lucide-react";
import { CAPABILITIES, BRAND, OFFICES } from "@/data/content";
import { INDUSTRY_LIST } from "@/data/industries";
import { scrollToId } from "@/lib/scroll";

const Footer = () => (
    <footer className="relative overflow-hidden border-t t-border t-bg" data-testid="site-footer">
        {/* Oversized VARASUN brand watermark — decorative background layer, emerges from the navy */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 select-none overflow-hidden">
            <span
                data-testid="footer-watermark"
                className="absolute inset-x-0 bottom-[7%] whitespace-nowrap text-center font-display text-[18vw] font-extrabold leading-none tracking-tight lg:text-[16vw]"
                style={{
                    color: "var(--t-heading)",
                    opacity: 0.05,
                    WebkitMaskImage: "linear-gradient(to bottom, transparent 10%, black 72%)",
                    maskImage: "linear-gradient(to bottom, transparent 10%, black 72%)",
                }}
            >
                VARASUN
            </span>
        </div>
        <div className="container-x relative z-10 grid gap-14 py-14 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
            <div>
                <Link to="/" data-testid="footer-logo" aria-label="Varasun Technologies home">
                    <img
                        src="/logo-main.webp"
                        alt="Varasun Technologies"
                        className="h-12 w-auto rounded-xl border t-border shadow-sm transition-transform duration-300 hover:scale-[1.05]"
                    />
                </Link>
                <p className="mt-6 max-w-sm text-sm leading-relaxed t-soft">
                    A trusted enterprise technology transformation partner — uniting strategy, engineering, security and
                    operations to turn complexity into competitive advantage.
                </p>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] t-accent">{BRAND.tagline}</p>
                <div className="mt-8 flex items-center gap-3">
                    <a
                        href={`mailto:${BRAND.email}`}
                        data-testid="footer-email"
                        aria-label="Email Varasun"
                        className="flex h-10 w-10 items-center justify-center rounded-full border t-border t-soft transition-colors hover:border-electric hover:text-electric"
                    >
                        <Mail size={16} />
                    </a>
                </div>
            </div>

            <nav aria-label="Capabilities">
                <h3 className="eyebrow t-faint">Capabilities</h3>
                <ul className="mt-5 space-y-3">
                    {CAPABILITIES.map((c) => (
                        <li key={c.code}>
                            <Link
                                to="/#capabilities"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (window.location.pathname === "/") scrollToId("capabilities");
                                    else window.location.href = "/#capabilities";
                                }}
                                className="text-sm t-soft transition-colors t-hover-heading"
                            >
                                {c.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <nav aria-label="Company">
                <h3 className="eyebrow t-faint">Company</h3>
                <ul className="mt-5 space-y-3">
                    {INDUSTRY_LIST.slice(0, 4).map((i) => (
                        <li key={i.slug}>
                            <Link to={`/industries/${i.slug}`} className="text-sm t-soft transition-colors t-hover-heading">
                                {i.name}
                            </Link>
                        </li>
                    ))}
                    <li>
                        <Link to="/about" data-testid="footer-about-link" className="text-sm t-soft transition-colors t-hover-heading">
                            About Varasun
                        </Link>
                    </li>
                    <li>
                        <Link to="/careers" data-testid="footer-careers-link" className="text-sm t-soft transition-colors t-hover-heading">
                            Careers
                        </Link>
                    </li>
                    <li>
                        <Link to="/insights" data-testid="footer-insights-link" className="text-sm t-soft transition-colors t-hover-heading">
                            Insights
                        </Link>
                    </li>
                    <li>
                        <a
                            href="/downloads/varasun-capability-deck.pptx"
                            data-testid="footer-deck-link"
                            className="text-sm t-soft transition-colors t-hover-heading"
                            download
                        >
                            Capability Deck (PPTX)
                        </a>
                    </li>
                    <li>
                        <Link to="/contact" data-testid="footer-contact-link" className="text-sm t-soft transition-colors t-hover-heading">
                            Contact
                        </Link>
                    </li>
                </ul>
            </nav>

            <div>
                <h3 className="eyebrow t-faint">Headquarters</h3>
                <ul className="mt-5 space-y-6">
                    {OFFICES.map((o) => (
                        <li key={o.entity} className="flex gap-3">
                            <MapPin size={16} className="mt-0.5 shrink-0 text-electric" />
                            <div>
                                <p className="text-sm font-semibold t-heading">{o.entity}</p>
                                <p className="mt-0.5 text-xs t-faint">{o.region}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <Link
                    to="/contact"
                    data-testid="footer-cta-link"
                    className="mt-8 inline-flex items-center gap-2 font-display text-sm font-semibold text-electric transition-colors hover:text-azure"
                >
                    Start the conversation <ArrowUpRight size={15} />
                </Link>
            </div>
        </div>

        <div className="relative z-10 border-t t-border">
            <div className="container-x flex flex-col items-start justify-between gap-3 py-6 md:flex-row md:items-center">
                <p className="text-xs t-faint">
                    © {new Date().getFullYear()} Varasun Technologies Inc. (USA) · Varasun Technologies India Pvt Ltd. All rights reserved.
                </p>
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] t-faint">
                    <Link to="/privacy" className="transition-colors hover:text-electric">Privacy</Link> ·
                    <Link to="/terms" className="transition-colors hover:text-electric">Terms</Link> ·
                    <Link to="/security" className="transition-colors hover:text-electric">Security</Link> ·
                    <Link to="/login" data-testid="footer-admin-link" className="transition-colors hover:text-electric">
                        Admin
                    </Link>
                </p>
            </div>
        </div>
    </footer>
);

export default Footer;
